import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Crosshair, CheckCircle2, ClipboardPlus, FolderOpen, ChevronDown } from 'lucide-react';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { Task } from '@/types/tasks';
import TaskCard from '../TaskCard';
import GlassPill from '@/components/ui/GlassPill';
import DashboardButton from '@/components/ui/DashboardButton';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { cardVariants } from '../animations/task-animations';
import useGoalsState from '@/hooks/goal-planner/useGoalsState';

interface ActiveTasksSectionProps {
  tasks: Task[];
  customTaskOperations?: {
    completeTask?: (taskId: string) => void;
    uncompleteTask?: (taskId: string) => void;
    removeTask?: (taskId: string) => void;
    updateTask?: (taskId: string, updates: Partial<Task>, silent?: boolean) => void;
  };
  onCreateTask?: () => void;
  // NEW: Project filter props
  selectedProjectId?: string | null;
  onProjectFilterChange?: (projectId: string | null) => void;
  allTasks?: any[];
  projectTaskCounts?: {
    projectCounts: Record<string, number>;
    totalTasks: number;
  };
}

const ActiveTasksSection: React.FC<ActiveTasksSectionProps> = React.memo(({ 
  tasks, 
  customTaskOperations, 
  onCreateTask,
  selectedProjectId = null,
  onProjectFilterChange,
  allTasks = [],
  projectTaskCounts = { projectCounts: {}, totalTasks: 0 }
}) => {
  // Get projects/goals data
  const { activeGoals, isLoading: isGoalsLoading } = useGoalsState();
  
  // Set up droppable area for active tasks
  const { setNodeRef } = useDroppable({
    id: 'active-tasks',
  });

  // Memoize task IDs to prevent SortableContext re-renders
  const taskIds = useMemo(() => tasks.map(task => task.id), [tasks]);

  // Memoize the task count to prevent unnecessary GlassPill re-renders
  const taskCount = useMemo(() => tasks.length, [tasks.length]);

  // Handle project filter change
  const handleProjectSelect = (projectId: string) => {
    const selectedId = projectId === 'all' ? null : projectId;
    if (onProjectFilterChange) {
      onProjectFilterChange(selectedId);
    }
  };

  // Get display text for selected project with count
  const selectedProjectText = useMemo(() => {
    if (!selectedProjectId) {
      return `All`;
    }
    const project = activeGoals.find(goal => goal.projectId === selectedProjectId);
    const count = projectTaskCounts.projectCounts[selectedProjectId] || 0;
    return `${project?.projectTitle || 'Unknown'} (${count})`;
  }, [selectedProjectId, activeGoals, projectTaskCounts]);

  return (
    <div
      ref={setNodeRef}
      className="relative p-3 md:p-6 rounded-lg overflow-hidden"
    >
      {/* Base Gradient (Bottom Layer) - matching card border radius */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/60 via-white/30 to-white/10 dark:from-black/40 dark:to-black/20 -z-10 rounded-lg" />
      
      {/* Top border highlight - matching card border radius */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-300/30 to-transparent dark:via-amber-500/20 rounded-t-lg" />
      
      {/* Glass effect - matching card border radius */}
      <div className="absolute inset-0 backdrop-blur-xl backdrop-saturate-150 -z-10 rounded-lg" />
      
      {/* Mobile: Stack vertically, Desktop: Horizontal layout */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3 md:mb-4">
        {/* Title Section */}
        <div className="flex items-center gap-2">
          <Crosshair className="text-amber-500 dark:text-amber-400" size={18} />
          <h3 className="font-medium text-base">Your Tasks</h3>
          {taskCount > 0 && (
            <GlassPill 
              type={taskCount.toString()} 
              variant="amber" 
              className="ml-2 text-xs h-6 w-6 flex items-center justify-center p-0"
            />
          )}
        </div>
        
        {/* Action Buttons - Stack on mobile, inline on desktop */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {/* New Task Button - styled like DashboardHeader primaryAction */}
          {onCreateTask && (
            <DashboardButton
              onClick={onCreateTask}
              icon={ClipboardPlus}
              size="sm"
              className="h-8 px-3 py-1.5 text-sm"
            >
              New Task
            </DashboardButton>
          )}
          
          {/* Project Filter Dropdown */}
          {onProjectFilterChange && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  className="h-8 px-3 bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20 hover:border-accent/30 justify-between"
                  aria-label="Filter tasks by project"
                >
                  <div className="flex items-center gap-2">
                    <FolderOpen size={14} className="text-accent" />
                    <span className="text-xs font-medium">
                      {selectedProjectText}
                    </span>
                  </div>
                  <ChevronDown size={12} className="text-accent/60 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="min-w-[200px]">
                <DropdownMenuItem 
                  onClick={() => handleProjectSelect('all')}
                  className="text-slate-700 dark:text-slate-200 focus:text-slate-700 dark:focus:text-slate-200 hover:text-slate-700 dark:hover:text-slate-200 focus:bg-accent/10 hover:bg-accent/10 px-4"
                >
                  <FolderOpen size={16} className="mr-2 text-gray-500" />
                  <span className="flex-1">All Projects</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    ({projectTaskCounts.totalTasks})
                  </span>
                </DropdownMenuItem>
                {isGoalsLoading ? (
                  <DropdownMenuItem disabled>
                    <span className="text-sm text-gray-500">Loading projects...</span>
                  </DropdownMenuItem>
                ) : activeGoals.length === 0 ? (
                  <DropdownMenuItem disabled>
                    <span className="text-sm text-gray-500">No projects found</span>
                  </DropdownMenuItem>
                ) : (
                  activeGoals
                    .filter(goal => goal.projectId && goal.projectTitle)
                    .filter((goal) => {
                      const taskCount = projectTaskCounts.projectCounts[goal.projectId!] || 0;
                      return taskCount > 0;
                    })
                    .map((goal) => {
                      const taskCount = projectTaskCounts.projectCounts[goal.projectId!] || 0;
                      return (
                        <DropdownMenuItem
                          key={goal.projectId}
                          onClick={() => handleProjectSelect(goal.projectId!)}
                          className="text-slate-700 dark:text-slate-200 focus:text-slate-700 dark:focus:text-slate-200 hover:text-slate-700 dark:hover:text-slate-200 focus:bg-accent/10 hover:bg-accent/10 px-4"
                        >
                          <FolderOpen size={16} className="mr-2 text-blue-500" />
                          <span className="flex-1">{goal.projectTitle}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                            ({taskCount})
                          </span>
                        </DropdownMenuItem>
                      );
                    })
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        <div className="space-y-2">
          {/* Removed initial animation to prevent dark flash */}
          <AnimatePresence initial={false} mode="popLayout">
            {taskCount === 0 ? (
              <EmptyActiveTasks />
            ) : (
              tasks.map((task) => (
                <div key={task.id}>
                  <TaskCard key={task.id} task={task} context="task-manager" customTaskOperations={customTaskOperations} />
                </div>
              ))
            )}
          </AnimatePresence>
        </div>
      </SortableContext>
    </div>
  );
});

ActiveTasksSection.displayName = 'ActiveTasksSection';

const EmptyActiveTasks = React.forwardRef<HTMLDivElement>((props, ref) => (
  <motion.div 
    ref={ref}
    className="flex flex-col items-center justify-center py-6"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.3 }}
    {...props}
  >
    <div className="relative mb-3">
      <div className="absolute inset-0 bg-gradient-to-r from-green-300/20 to-blue-300/20 dark:from-green-500/10 dark:to-blue-500/10 rounded-full blur-lg"></div>
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ 
          repeat: Infinity, 
          repeatType: "reverse", 
          duration: 2,
          ease: "easeInOut"
        }}
      >
        <div className="relative z-10 bg-gradient-to-br from-green-400 to-blue-400 dark:from-green-500 dark:to-blue-500 p-3 rounded-full">
          <CheckCircle2 size={32} className="h-8 w-8 text-white" />
        </div>
      </motion.div>
    </div>
    
    <h4 className="text-base font-semibold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-blue-500 dark:from-green-400 dark:to-blue-400 mb-1">
      All caught up!
    </h4>
    
    <p className="text-sm px-4 py-2 rounded-lg bg-white/50 dark:bg-white/5 backdrop-blur-sm border border-white/20 dark:border-white/10">
      Great job! You've completed all your active tasks.
    </p>
  </motion.div>
));

EmptyActiveTasks.displayName = 'EmptyActiveTasks';

export default ActiveTasksSection;
